import { useState } from "react";
import type { FormEvent } from "react";
import type { SeasonManagerAliases, Team, TeamManagerAlias } from "@averagejoesff/shared";
import { upsertManagerAliases } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";

interface EditManagerNamesFormProps {
  season: number;
  /** This season's roster, read-only here — names/managers come from Yahoo. */
  teams: Team[];
  /**
   * Already-loaded aliases for this season (or `null` if none saved yet) —
   * the caller must only render this form once its `useManagerNames`
   * resource is `"ready"`, so `useState`'s initializer below sees the real
   * data on first mount instead of racing an in-flight fetch.
   */
  currentAliases: SeasonManagerAliases | null;
  idToken: string;
  onSaved: (aliases: SeasonManagerAliases) => void;
}

interface Row {
  name: string;
  yahooManagers: string;
  override: string;
}

function toRows(teams: Team[], currentAliases: SeasonManagerAliases | null): Row[] {
  const overrideByName = new Map(currentAliases?.teams.map((t) => [t.name, t.managerName]) ?? []);
  return teams.map((t) => ({
    name: t.name,
    yahooManagers: t.managerNames.join(", "),
    override: overrideByName.get(t.name) ?? "",
  }));
}

function toManagerAliases(rows: Row[]): TeamManagerAlias[] {
  return rows.map((r) => ({ name: r.name, managerName: r.override.trim() }));
}

/**
 * A fixed row per existing team — unlike `EditTeamsForm`, this never
 * adds/removes/renames a team (the roster itself always comes from Yahoo);
 * it only lets an admin set a real-name override that survives the next
 * weekly fetch. Leaving the field blank falls back to Yahoo's own
 * nickname(s), shown as a hint.
 */
export function EditManagerNamesForm({
  season,
  teams,
  currentAliases,
  idToken,
  onSaved,
}: EditManagerNamesFormProps) {
  const [rows, setRows] = useState<Row[]>(() => toRows(teams, currentAliases));
  const { status, error, submit } = useAdminForm(
    () => upsertManagerAliases(season, toManagerAliases(rows), idToken),
    onSaved,
  );

  function updateOverride(index: number, override: string) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, override } : r)));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  if (teams.length === 0) {
    return <p className="blowout-table__empty">No teams on this season's roster yet.</p>;
  }

  return (
    <form className="admin-panel__form" onSubmit={handleSubmit}>
      <ul className="admin-panel__team-list">
        {rows.map((row, i) => (
          <li key={row.name} className="admin-panel__team-row">
            <div className="admin-panel__team-row-fields">
              <span className="admin-panel__team-row-label">{row.name}</span>
              <input
                type="text"
                placeholder={row.yahooManagers || "No Yahoo manager set"}
                value={row.override}
                onChange={(e) => updateOverride(i, e.target.value)}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="admin-panel__actions">
        <button type="submit" disabled={status === "pending"}>
          {status === "pending" ? "Saving…" : "Save manager names"}
        </button>
        <AdminFormStatus
          status={status}
          error={error}
          savedLabel="Saved — other viewers may take up to a minute to see it."
        />
      </div>
    </form>
  );
}
